/** Previews stay paused. Audience playback opts in when the video is revealed. */
export function youtubeEmbedUrl(
  videoId: string,
  startValue?: string | boolean,
  _legacyDurationValue?: string | boolean,
): string {
  const parameters = new URLSearchParams({rel: '0', playsinline: '1', start: String(videoStart(startValue))});
  if (typeof location !== 'undefined' && /^https?:$/.test(location.protocol)) {
    parameters.set('origin', location.origin);
  }
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${parameters}`;
}

/** Preserve the approved timestamp; autoplay belongs to the active audience player. */
export function autoplayEmbedUrl(source: string): string {
  const url = new URL(source);
  url.searchParams.set('autoplay', '1');
  return url.href;
}

export function videoStart(value?: string | boolean): number {
  const seconds = typeof value === 'string' ? Number(value) : 0;
  return Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
}

export function createVideo(source: string, title: string, attrs: Record<string, string | boolean>): Element {
  if (source.startsWith('youtube:')) {
    const iframe = document.createElement('iframe');
    iframe.dataset.playbackSrc = youtubeEmbedUrl(source.slice(8), attrs.start);
    iframe.title = title || 'Video';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    return iframe;
  }
  if (source.startsWith('local:')) {
    const placeholder = document.createElement('div');
    placeholder.className = 'local-video-placeholder';
    placeholder.textContent = `Choose local video: ${source.slice(6)}`;
    return placeholder;
  }
  const video = document.createElement('video');
  video.dataset.playbackSrc = source;
  video.dataset.playbackStart = String(videoStart(attrs.start));
  video.controls = true;
  video.playsInline = true;
  video.preload = 'metadata';
  return video;
}

export function videoLink(source: string, attrs: Record<string, string | boolean>): HTMLAnchorElement | undefined {
  if (!source.startsWith('youtube:')) return;
  const link = document.createElement('a');
  link.className = 'video-external-link';
  link.href = `https://www.youtube.com/watch?v=${encodeURIComponent(source.slice(8))}&t=${videoStart(attrs.start)}s`;
  link.textContent = 'Open on YouTube ↗';
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  return link;
}
